import type { Persona } from '@/shared/types';
import { controlLoops } from '@/versions/v1/modules/process/Scene.transform';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import { CASES, FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { formatClockShort } from '@/versions/v1/shared/clock.derive';
import { SHIFT_CHECKS, type ShiftCheck, type ShiftCheckKind } from './seed';
import { MANUAL_LOOP_LIMIT_HOURS, manualLoopBreaches } from './Scene.transform';

/** One reason a check is at risk, as the glass reads it: a loop past the
 *  manual rule, or an open case (its code links into Advisory, adjudication 7). */
export type ShiftCheckReason =
  | { kind: 'loop'; text: string }
  | { kind: 'case'; caseId: string; code: string; state: string };

export type ShiftCheckRow = {
  id: string;
  title: string;
  description: string;
  detailLine?: string;
  reasons: readonly ShiftCheckReason[];
  frequency: 'Each shift';
  riskLevel: 'high' | 'medium';
  riskLevelLabel: 'HIGH RISK' | 'MED RISK';
  riskLevelTone: 'warning' | 'default';
  kind: ShiftCheckKind;
  ownerPersona: Persona;
  failureModeLabels: readonly string[];
  done: boolean;
  atRisk: boolean;
};

export type ShiftChecklistView = {
  header: string;
  /** `08 Jun · Day shift` — the header's clock half. */
  shiftLabel: string;
  done: number;
  total: number;
  atRisk: number;
  rows: readonly ShiftCheckRow[];
};

function kindRank(kind: ShiftCheckKind, persona: Persona): number {
  switch (persona) {
    case 'Operator':
    case 'Control room':
      return kind === 'field' ? 0 : 1;
    case 'Metallurgist':
    case 'Senior metallurgist':
      if (kind === 'reagent') return 0;
      if (kind === 'loop') return 1;
      return 2;
    case 'Maintenance':
      return kind === 'equipment' ? 0 : 1;
    default:
      return 0;
  }
}

function orderChecks(checks: readonly ShiftCheck[], persona: Persona): readonly ShiftCheck[] {
  return checks
    .map((check, index) => ({ check, index }))
    .sort((left, right) => {
      const rank = kindRank(left.check.kind, persona) - kindRank(right.check.kind, persona);
      if (rank !== 0) return rank;
      return left.index - right.index;
    })
    .map((entry) => entry.check);
}

function caseOpen(caseId: string): { code: string; state: string } | undefined {
  const found = CASES.find((item) => item.id === caseId);
  if (!found || found.state === 'closed') return undefined;
  return { code: found.code ?? found.id, state: found.state };
}

/** `open`, or `open · evidence requested` when the case's state says more. */
export function caseStateText(state: string): string {
  return state === 'open' ? 'open' : `open · ${state.replace(/-/g, ' ')}`;
}

export function shiftChecklist(persona: Persona): ShiftChecklistView {
  const breaches = manualLoopBreaches(controlLoops());
  const doneCount = SHIFT_CHECKS.filter((check) => check.done).length;
  const clock = formatClockShort(DEFAULT_CURSOR).split(' · ').slice(0, 2).join(' · ');
  const header = `${clock} · ${doneCount}/${SHIFT_CHECKS.length} complete`;

  const rows = orderChecks(SHIFT_CHECKS, persona).map((check) => {
    const loopDetails = check.loopTags.flatMap((tag) => {
      const loop = breaches.find((item) => item.tag === tag);
      if (!loop) return [];
      return [`${loop.tag} ${loop.mode} ${loop.duration} · rule ${MANUAL_LOOP_LIMIT_HOURS} h`];
    });
    const caseReasons = check.caseIds.flatMap((caseId): ShiftCheckReason[] => {
      const open = caseOpen(caseId);
      if (!open) return [];
      return [{ kind: 'case', caseId, code: open.code, state: caseStateText(open.state) }];
    });
    const caseDetails = caseReasons.map((reason) => reason.kind === 'case' ? `Case ${reason.code} ${reason.state}` : '');
    const loopBreach = loopDetails.length > 0;
    const caseIsOpen = caseDetails.length > 0;
    const detailParts = [...loopDetails, ...caseDetails];
    const failureModeLabels = check.failureModeIds.flatMap((id) => {
      const mode = FAILURE_MODES.find((item) => item.id === id);
      return mode ? [mode.failureMode] : [];
    });
    return {
      id: check.id,
      title: check.title,
      description: check.description,
      detailLine: detailParts.length > 0 ? detailParts.join(' · ') : undefined,
      reasons: [...loopDetails.map((text): ShiftCheckReason => ({ kind: 'loop', text })), ...caseReasons],
      frequency: check.frequency,
      riskLevel: check.riskLevel,
      riskLevelLabel: (check.riskLevel === 'high' ? 'HIGH RISK' : 'MED RISK') as ShiftCheckRow['riskLevelLabel'],
      riskLevelTone: (check.riskLevel === 'high' ? 'warning' : 'default') as ShiftCheckRow['riskLevelTone'],
      kind: check.kind,
      ownerPersona: check.ownerPersona,
      failureModeLabels,
      done: check.done,
      atRisk: loopBreach || caseIsOpen,
    };
  });

  return {
    header,
    shiftLabel: clock,
    done: doneCount,
    total: SHIFT_CHECKS.length,
    atRisk: rows.filter((row) => row.atRisk).length,
    rows,
  };
}
